"use client";

import React from "react";
import { ArrowRight, Calendar, User, Tag } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";

declare const wvcClient: any;

function SinglePostConsumer() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div data-section-id="221731" className="max-w-4xl mx-auto animate-pulse">
        <div className="h-12 bg-muted rounded-md w-3/4 mx-auto mb-6"></div>
        <div className="h-6 bg-muted rounded-md w-1/2 mx-auto mb-10"></div>
        <div className="aspect-video bg-muted rounded-xl mb-12"></div>
        <div className="space-y-4">
          <div className="h-4 bg-muted rounded w-full"></div>
          <div className="h-4 bg-muted rounded w-full"></div>
          <div className="h-4 bg-muted rounded w-5/6"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto text-center py-20">
        <p className="text-destructive font-default">Failed to load the roofing article. Please try again later.</p>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="max-w-4xl mx-auto text-center py-20">
        <h1 className="font-default text-3xl font-bold mb-4">Article Not Found</h1>
        <p className="text-muted-foreground font-default mb-8">The roofing or waterproofing guide you are looking for does not exist or has been moved.</p>
        <Link to="#">
          <Button className="bg-primary text-primary-foreground hover:bg-primary/90">Return to Homepage</Button>
        </Link>
      </div>
    );
  }

  const post = posts[0];

  return (
    <div className="max-w-4xl mx-auto">
      {}
      <header className="text-center mb-12">
        {post.categories && post.categories.length > 0 && (
          <div className="flex justify-center gap-2 mb-6 flex-wrap">
            {post.categories.map((cat, i) => (
              <span key={cat.id} data-index={i} className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-accent text-accent-foreground text-xs font-semibold uppercase tracking-wider font-default">
                <Tag className="w-3 h-3" />
                {cat.name}
              </span>
            ))}
          </div>
        )}
        
        <h1 className="font-default text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight text-foreground">
          {post.title}
        </h1>
        
        <div className="flex flex-wrap justify-center items-center gap-6 text-muted-foreground font-default text-sm">
          {post.author && (
            <div className="flex items-center gap-2">
              <User className="w-4 h-4" />
              <span>{post.author.name}</span>
            </div>
          )}
          {post.date && (
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              <span>{new Date(post.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
            </div>
          )}
        </div>
      </header>

      {}
      {post.featuredImage && (
        <div className="mb-16 rounded-lg overflow-hidden aspect-video bg-muted shadow-xl">
          <img 
            src={post.featuredImage} 
            alt={post.title} 
            className="w-full h-full object-cover"
          />
        </div>
      )}

      {}
      <div 
        className="prose prose-lg max-w-none font-default text-foreground/90 leading-relaxed mb-16 prose-headings:font-default prose-headings:font-bold prose-headings:text-foreground prose-a:text-ring hover:prose-a:text-ring/80 prose-img:rounded-lg"
        dangerouslySetInnerHTML={{ __html: post.content || "" }}
      />

      <Separator className="my-12 bg-border" />

      {}
      {post.author && (
        <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6 bg-card p-8 rounded-lg border border-border shadow-md">
          <Avatar className="h-24 w-24 border-2 border-primary/10">
            <AvatarImage src={post.author.avatar?.["96"]} alt={post.author.name} />
            <AvatarFallback className="text-2xl bg-muted text-primary font-default">
              {post.author.name.charAt(0)}
            </AvatarFallback>
          </Avatar>
          <div className="text-center sm:text-left flex-1">
            <h3 className="font-default text-2xl font-bold mb-2 text-foreground">Expertise by {post.author.name}</h3>
            <p className="text-muted-foreground font-default leading-relaxed mb-4">
              {}
              Specialist in roofing systems and waterproofing technology. Dedicated to providing durable building solutions and technical insights for property protection.
            </p>
            {post.author.link && (
              <Link to={post.author.link}>
                <Button variant="outline" size="sm" className="font-default border-primary text-primary hover:bg-primary hover:text-primary-foreground">
                  View all insights
                </Button>
              </Link>
            )}
          </div>
        </div>
      )}

      <Separator className="my-16 bg-border" />

      {}
      <RelatedPosts currentPostId={post.id} />
    </div>
  );
}

function RelatedPostsConsumer() {
  const { posts, loading } = usePosts();

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {[1, 2].map((_, i) => (
          <div key={i} data-index={i} className="h-80 bg-muted rounded-lg animate-pulse"></div>
        ))}
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return null;
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
      {posts.map((p, i) => (
        <Card key={p.id} data-index={i} className="bg-card border-border overflow-hidden flex flex-col hover:shadow-lg transition-shadow duration-300 rounded-lg">
          {p.featuredImage && (
            <Link to={p.link || "#"} className="block aspect-video w-full overflow-hidden">
              <img 
                src={p.featuredImage} 
                alt={p.title} 
                className="w-full h-full object-cover transition-transform duration-500 hover:scale-105" 
              />
            </Link>
          )}
          <CardHeader className="pb-3">
            <Link to={p.link || "#"}>
              <CardTitle className="font-default text-2xl line-clamp-2 hover:text-ring transition-colors">
                {p.title}
              </CardTitle>
            </Link>
          </CardHeader>
          <CardContent className="flex-grow pb-4">
            <div 
              className="text-muted-foreground font-default text-sm line-clamp-3" 
              dangerouslySetInnerHTML={{ __html: p.excerpt || "" }} 
            />
          </CardContent>
          <CardFooter className="pt-0">
            <Link to={p.link || "#"}>
              <Button variant="link" className="p-0 h-auto text-primary font-default font-semibold group">
                Read Full Guide 
                <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
              </Button>
            </Link>
          </CardFooter>
        </Card>
      ))}
    </div>
  );
}

function RelatedPosts({ currentPostId }: { currentPostId: string }) {
  
  const wp_query = new WP_Query({ 
    post_type: 'post', 
    per_page: 2,
    post__not_in: [currentPostId] 
  });

  return (
    <div>
      <h2 className="font-default text-3xl font-bold mb-8 text-center sm:text-left text-foreground">Related Roofing Guides</h2>
      <PostsProvider wp_query={wp_query}>
        <RelatedPostsConsumer />
      </PostsProvider>
    </div>
  );
}

export default function SinglePostTemplateSection() {
  
  const currentWPQueryParams = typeof window !== 'undefined' && wvcClient ? wvcClient.getCurrentWPQueryParams() : { post_type: 'post', per_page: 1 };
  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section id="single-post-template-section" className="bg-background text-foreground py-20 px-4 md:px-8 min-h-screen">
      <PostsProvider wp_query={wp_query}>
        <SinglePostConsumer />
      </PostsProvider>
    </section>
  );
}